import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useChat } from '../app/ChatContext';
import { useStore } from '../app/StoreContext';
import { useApp } from '../app/AppContext';

/** Floating chat button + popup panel, available on every page of a store. */
export function ChatPopup() {
  const { slug, store, addToCart } = useStore();
  const { authed, toast } = useApp();
  const chat = useChat();
  const [input, setInput] = useState('');
  const bottom = useRef<HTMLDivElement | null>(null);

  useEffect(() => { if (chat.open) bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat.messages, chat.open, chat.transcript]);
  useEffect(() => {
    if (!chat.open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') chat.setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chat]);

  if (!store) return null;
  const voiceOn = chat.voiceState !== 'idle';
  const stateLabel = { idle: '', listening: 'Listening…', thinking: 'Thinking…', speaking: 'Speaking…', paused: 'Paused' }[chat.voiceState];

  return (
    <>
      {!chat.open && (
        <button className="chat-fab" onClick={() => chat.setOpen(true)} aria-label={`Chat with ${store.displayName}`}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z"/></svg>
        </button>
      )}
      {chat.open && (
        <div className="chat-popup" role="dialog" aria-label={`Chat with ${store.displayName}`}>
          <header className="chat-popup-head">
            <div className="row" style={{ minWidth: 0 }}>
              {store.branding.logoUrl && <img className="chat-avatar" src={store.branding.logoUrl} alt="" />}
              <div style={{ minWidth: 0 }}>
                <div className="chat-title">{store.displayName}</div>
                <div className={`muted small chat-state chat-state-${chat.voiceState}`}>{voiceOn ? stateLabel : (chat.busy ? 'Typing…' : 'Ask about the menu or order by chat')}</div>
              </div>
            </div>
            <div className="row">
              {chat.available && chat.voiceSupported && (
                <button className={`btn btn-sm ${voiceOn ? 'btn-danger' : 'btn-ghost'}`} onClick={() => (voiceOn ? chat.stopVoice() : chat.startVoice())} aria-pressed={voiceOn} title={voiceOn ? 'Stop voice' : 'Talk instead of typing'}>
                  {voiceOn ? '■ Stop' : '🎤 Voice'}
                </button>
              )}
              <button className="btn btn-sm btn-ghost" onClick={() => chat.setOpen(false)} aria-label="Close chat">✕</button>
            </div>
          </header>

          <div className="chat-log">
            {!authed && <p className="muted center" style={{ padding: 12 }}>Starting your session…</p>}
            {authed && !chat.available && (
              <div className="callout" style={{ margin: 8 }}>
                <p>Your account belongs to another store. Sign in with a {store.displayName} account to chat here.</p>
                <Link className="btn btn-sm" to={`/${slug}/login`} state={{ returnTo: `/${slug}` }} onClick={() => chat.setOpen(false)}>Sign in</Link>
              </div>
            )}
            {chat.available && chat.messages.length === 0 && !chat.transcript && (
              <p className="muted center" style={{ padding: 12 }}>Ask about the menu, specials, or say what you'd like to order.{chat.voiceSupported ? ' Tap Voice to talk.' : ''}</p>
            )}
            {chat.messages.map((m, i) => (
              <div key={i} className={`bubble bubble-${m.role}`}>
                {m.text || (chat.busy && i === chat.messages.length - 1 ? <span className="dots" aria-label="Thinking">…</span> : '')}
                {m.suggestions && (
                  <div className="chips" style={{ marginTop: 8 }}>
                    {m.suggestions.map((s) => (
                      <button key={s.item} className="chip" onClick={() => { addToCart(s.item, 1); toast(`${s.item} added to cart`, 'ok'); }}>+ {s.item}{s.price ? ` · ${s.price}` : ''}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {chat.transcript && <div className="bubble bubble-user bubble-live">{chat.transcript}</div>}
            <div ref={bottom} />
          </div>

          {chat.error && <p className="error small" style={{ padding: '0 12px' }}>{chat.error}</p>}
          {voiceOn && (
            <div className={`voice-bar voice-${chat.voiceState}`}>
              <span className="voice-dot" aria-hidden />
              <span>{stateLabel}</span>
              {chat.voiceState !== 'listening' && <button className="link small" onClick={() => chat.cancel()}>Cancel</button>}
            </div>
          )}
          <form className="chat-input" onSubmit={(e) => { e.preventDefault(); const t = input; setInput(''); void chat.send(t); }}>
            <input className="input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={chat.available ? (chat.ready ? 'Message…' : 'Connecting…') : 'Sign in to chat'} disabled={!chat.available || !chat.ready} aria-label="Message" />
            <button className="btn" type="submit" disabled={!chat.available || !chat.ready || chat.busy || !input.trim()}>Send</button>
          </form>
        </div>
      )}
    </>
  );
}
